import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn } from '@angular/common/http';
import { PLATFORM_ID, inject } from '@angular/core';
import { EMPTY } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Stops authenticated API calls from being made while the app is server-rendered.
 *
 * The bearer token lives in `localStorage`, which does not exist on the server, so
 * `ApiService.setHeaders` cannot attach an Authorization header there. Every API call made
 * during SSR therefore went out bare and came back 401 — which not only filled the server
 * log with noise but tripped the 401 branch of `httpErrorInterceptor` and redirected the
 * render to /login.
 *
 * Short-circuiting here means components no longer need their own `isPlatformBrowser`
 * guard before fetching: the request never leaves the server, the component keeps its
 * loading state, and the browser issues the real request after hydration.
 *
 * Static assets are left alone — i18n bundles must still resolve during SSR or the
 * server-rendered HTML comes out untranslated.
 */
export const ssrApiSkipInterceptor: HttpInterceptorFn = (req, next) => {
  if (isPlatformBrowser(inject(PLATFORM_ID))) {
    return next(req);
  }

  if (req.url.includes('/assets/')) {
    return next(req);
  }

  const apiUrl = environment.apiUrl ?? '';
  const isApiCall = !!apiUrl && req.url.startsWith(apiUrl);

  return isApiCall ? EMPTY : next(req);
};
